import { FormProvider, useForm, useWatch, Controller } from 'react-hook-form';
import type { UseFormProps, Control } from 'react-hook-form';
import { DEFAULT_FORM_CONFIG } from '@/constants';
import { notify } from '@/functions';
import { AppVersionOpEnum, BulkNotificationCampaignTypeEnum, MobilePlatformEnum } from '@/enums';
import { GenderEnum } from '@/lib/enum';
import type { IBulkNotificationFormState, ICreateBulkNotificationCampaignPayload } from '@/interfaces';
import { useRequestCreateBulkNotificationCampaign } from '@/hooks/httpRequest';
import { ControlledInput, ControlledSelect } from '@/components/controlled';
import { Button, Calendar, Checkbox, Label, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { CountryMultiSelect } from '@/pages/adminMessagesPage/components';
import { CampaignsList, UninstallsCard } from './components';
import { useCallback, useState } from 'react';
import { CalendarIcon, Send } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const defaultValues: IBulkNotificationFormState = {
	activeLang: 'FR',
	titleFr: '',
	titleEn: '',
	messageFr: '',
	messageEn: '',
	campaignType: BulkNotificationCampaignTypeEnum.ONE_TIME,
	scheduledAt: undefined,
	startDate: undefined,
	endDate: undefined,
	intervalDays: '',
	filterGender: [GenderEnum.MALE, GenderEnum.FEMALE],
	filterPlatform: [MobilePlatformEnum.IOS, MobilePlatformEnum.ANDROID],
	filterMinCoins: '',
	filterCreatedAtMax: undefined,
	filterCountries: [],
	filterAppVersion: '',
	filterAppVersionOp: AppVersionOpEnum.GTE,
};

const CAMPAIGN_TYPE_OPTIONS = [
	{ value: BulkNotificationCampaignTypeEnum.ONE_TIME, title: 'Une fois' },
	{ value: BulkNotificationCampaignTypeEnum.RECURRENT, title: 'Récurrent' },
];

const GENDER_OPTIONS = [
	{ value: GenderEnum.MALE, label: 'Hommes' },
	{ value: GenderEnum.FEMALE, label: 'Femmes' },
] as const;

const PLATFORM_OPTIONS = [
	{ value: MobilePlatformEnum.IOS, label: 'iOS' },
	{ value: MobilePlatformEnum.ANDROID, label: 'Android' },
] as const;

const inputClass = 'w-full rounded-lg border border-border bg-white px-3 py-1.5 text-sm focus:outline-hidden focus:ring-1 focus:ring-foreground placeholder:text-muted-foreground/60';
const textareaClass = 'w-full rounded-lg border border-border bg-white px-3 py-1.5 text-sm focus:outline-hidden focus:ring-1 focus:ring-foreground placeholder:text-muted-foreground/60 resize-none';
const sectionLabelClass = 'text-xs font-medium text-muted-foreground uppercase tracking-wide';

interface DateTimePickerProps {
	label: string;
	field: 'scheduledAt' | 'startDate' | 'endDate';
	control: Control<IBulkNotificationFormState>;
	required?: boolean;
	disabledBefore?: Date;
	validate?: (date: Date | undefined) => string | boolean | undefined;
}

const DateTimePicker = ({ label, field, control, required, disabledBefore, validate }: DateTimePickerProps) => (
	<div className="space-y-1">
		<label className="text-xs font-semibold text-muted-foreground">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
		<Controller
			name={field}
			control={control}
			rules={{ ...(required ? { required: 'Requis' } : {}), ...(validate ? { validate } : {}) }}
			render={({ field: { value, onChange }, fieldState: { error } }) => (
				<>
					<Popover>
						<PopoverTrigger asChild>
							<Button
								type="button"
								variant="outline"
								className={cn(
									'w-full justify-start text-left font-normal text-xs px-3',
									!value && 'text-muted-foreground',
									error && 'border-red-500',
								)}
							>
								<CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
								{value ? format(value, 'dd/MM/yyyy HH:mm', { locale: fr }) : 'Choisir une date'}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0">
							<Calendar
								mode="single"
								selected={value}
								onSelect={d => {
									if (!d) return onChange(undefined);
									const next = new Date(d);
									if (value) { next.setHours(value.getHours(), value.getMinutes()); }
									onChange(next);
								}}
								autoFocus
								disabled={disabledBefore ? (d) => { const s = new Date(disabledBefore); s.setHours(0,0,0,0); return d < s; } : (d) => { const today = new Date(); today.setHours(0,0,0,0); return d < today; }}
							/>
							<div className="border-t p-3">
								<input
									type="time"
									className="w-full rounded-md border px-3 py-1.5 text-sm"
									value={value ? format(value, 'HH:mm') : ''}
									onChange={e => {
										const [h, m] = e.target.value.split(':').map(Number);
										const next = value ? new Date(value) : new Date();
										next.setHours(h, m, 0, 0);
										onChange(next);
									}}
								/>
							</div>
						</PopoverContent>
					</Popover>
					{error && <p className="text-xs text-destructive">{error.message}</p>}
				</>
			)}
		/>
	</div>
);

const BulkNotificationPage = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const formMethods = useForm<IBulkNotificationFormState>({
		...(DEFAULT_FORM_CONFIG as UseFormProps<IBulkNotificationFormState>),
		defaultValues,
	});

	const { handleSubmit, control, reset, register, setValue, getValues, watch, formState: { errors } } = formMethods;
	const activeLang = useWatch({ control, name: 'activeLang' }) ?? 'FR';
	const campaignType = useWatch({ control, name: 'campaignType' });
	const filterGender = (useWatch({ control, name: 'filterGender' }) ?? []) as string[];
	const filterPlatform = (useWatch({ control, name: 'filterPlatform' }) ?? []) as string[];
	const filterAppVersionOp = useWatch({ control, name: 'filterAppVersionOp' }) ?? AppVersionOpEnum.GTE;
	const isFR = activeLang === 'FR';

	const startDate = watch('startDate');

	const toggleGender = useCallback((value: string) => {
		const current = getValues('filterGender') || [];
		setValue(
			'filterGender',
			current.includes(value) ? current.filter(g => g !== value) : [...current, value],
			{ shouldDirty: true },
		);
	}, [getValues, setValue]);

	const togglePlatform = useCallback((value: string) => {
		const current = getValues('filterPlatform') || [];
		setValue(
			'filterPlatform',
			current.includes(value) ? current.filter((p: string) => p !== value) : [...current, value],
			{ shouldDirty: true },
		);
	}, [getValues, setValue]);

	const { createCampaign } = useRequestCreateBulkNotificationCampaign({
		onSuccess: () => {
			notify('Campagne créée.');
			reset(defaultValues);
		},
	});

	const buildPayload = (data: IBulkNotificationFormState): ICreateBulkNotificationCampaignPayload => {
		const filters: ICreateBulkNotificationCampaignPayload['filters'] = {};
		if (data.filterGender?.length && data.filterGender.length < GENDER_OPTIONS.length) filters.gender = data.filterGender as GenderEnum[];
		if (data.filterPlatform?.length === 1) filters.platform = data.filterPlatform[0] as MobilePlatformEnum;
		if (data.filterMinCoins) filters.minCoins = Number(data.filterMinCoins);
		if (data.filterCreatedAtMax) filters.createdAtMax = data.filterCreatedAtMax.toISOString();
		if (data.filterCountries?.length) filters.countryIso3 = data.filterCountries;
		if (data.filterAppVersion?.trim()) {
			filters.appVersion = data.filterAppVersion.trim();
			filters.appVersionOp = data.filterAppVersionOp ?? AppVersionOpEnum.GTE;
		}

		const payload: ICreateBulkNotificationCampaignPayload = {
			title: { fr: data.titleFr, en: data.titleEn },
			message: { fr: data.messageFr, en: data.messageEn },
			campaignType: data.campaignType as BulkNotificationCampaignTypeEnum,
			...(Object.keys(filters).length > 0 ? { filters } : {}),
		};

		if (data.campaignType === BulkNotificationCampaignTypeEnum.ONE_TIME) {
			payload.scheduledAt = data.scheduledAt?.toISOString();
		} else {
			payload.startDate = data.startDate?.toISOString();
			payload.endDate = data.endDate?.toISOString();
			payload.intervalDays = Number(data.intervalDays);
		}

		return payload;
	};

	const onSubmit = handleSubmit(async data => {
		try {
			setIsSubmitting(true);
			await createCampaign(buildPayload(data));
		} catch (e) {
			const msg = e instanceof Error ? e.message : 'Erreur lors de la création.';
			notify((e as Record<string, string>)?.internalMessage || msg, 'error');
		} finally {
			setIsSubmitting(false);
		}
	});

	const hasContentError = isFR
		? !!(errors.titleFr || errors.messageFr)
		: !!(errors.titleEn || errors.messageEn);

	return (
		<FormProvider {...formMethods}>
			<div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">

				{/* Col 1 — Filters */}
				<div className="lg:col-span-3 space-y-4">
					<div className="bg-white rounded-2xl shadow-soft border border-border">
						<div className="px-4 py-3 border-b border-border">
							<h2 className="font-semibold">Filtres</h2>
						</div>
						<div className="p-4 space-y-4">
							<div className="space-y-2">
								<p className={sectionLabelClass}>Genre</p>
								<div className="flex gap-4">
									{GENDER_OPTIONS.map(({ value, label }) => (
										<Label key={value} className="flex items-center gap-2 cursor-pointer text-sm font-normal">
											<Checkbox
												checked={filterGender.includes(value)}
												onCheckedChange={() => toggleGender(value)}
											/>
											{label}
										</Label>
									))}
								</div>
								{(filterGender.length === 0 || filterGender.length === GENDER_OPTIONS.length) && (
									<p className="text-[11px] text-muted-foreground">Tous les genres inclus</p>
								)}
							</div>

							<div className="border-t border-border" />

							<div className="space-y-2">
								<p className={sectionLabelClass}>Plateforme</p>
								<div className="flex gap-4">
									{PLATFORM_OPTIONS.map(({ value, label }) => (
										<Label key={value} className="flex items-center gap-2 cursor-pointer text-sm font-normal">
											<Checkbox
												checked={filterPlatform.includes(value)}
												onCheckedChange={() => togglePlatform(value)}
											/>
											{label}
										</Label>
									))}
								</div>
							{(filterPlatform.length === 0 || filterPlatform.length === PLATFORM_OPTIONS.length) && (
								<p className="text-[11px] text-muted-foreground">Toutes les plateformes incluses</p>
							)}
							</div>

							<div className="border-t border-border" />

							<div className="space-y-1">
								<p className={sectionLabelClass}>Version</p>
								<div className="flex gap-2">
									<label htmlFor="filterAppVersionOp" className="sr-only">Opérateur de version</label>
									<select
										id="filterAppVersionOp"
										className="h-10 w-19 shrink-0 rounded-lg border border-border bg-white px-2 text-sm focus:outline-hidden focus:ring-1 focus:ring-foreground"
										{...register('filterAppVersionOp')}
									>
										<option value={AppVersionOpEnum.GTE}>&gt;=</option>
										<option value={AppVersionOpEnum.LTE}>&lt;=</option>
									</select>
									<label htmlFor="filterAppVersion" className="sr-only">Numéro de version</label>
									<ControlledInput
										id="filterAppVersion"
										control={control}
										name="filterAppVersion"
										placeholder="5.1.6"
										className="flex-1"
										autoComplete="off"
										inputMode="decimal"
									/>
								</div>
								<p className="text-[11px] text-muted-foreground">
									{filterAppVersionOp === AppVersionOpEnum.LTE
										? 'Cette version et en-dessous'
										: 'Cette version et au-dessus'}
								</p>
							</div>

							<div className="border-t border-border" />

							<div className="space-y-1">
								<p className={sectionLabelClass}>Coins minimum</p>
								<ControlledInput control={control} name="filterMinCoins" type="number" placeholder="0" min={0} />
							</div>

							<div className="space-y-1">
								<p className={sectionLabelClass}>Compte créé avant</p>
								<Controller
									name="filterCreatedAtMax"
									control={control}
									render={({ field: { value, onChange } }) => (
										<Popover>
											<PopoverTrigger asChild>
												<Button
													type="button"
													variant="outline"
													className={cn(
														'w-full justify-start text-left font-normal text-xs px-3',
														!value && 'text-muted-foreground',
													)}
												>
													<CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
													{value ? format(value, 'dd MMM yyyy', { locale: fr }) : 'Choisir une date'}
												</Button>
											</PopoverTrigger>
											<PopoverContent className="w-auto p-0">
												<Calendar mode="single" selected={value} onSelect={onChange} autoFocus />
											</PopoverContent>
										</Popover>
									)}
								/>
								{watch('filterCreatedAtMax') && (
									<button
										type="button"
										className="text-[11px] text-muted-foreground hover:text-foreground"
										onClick={() => setValue('filterCreatedAtMax', undefined)}
									>
										Effacer
									</button>
								)}
							</div>

							<div className="border-t border-border" />

							<CountryMultiSelect name="filterCountries" />
						</div>
					</div>
				</div>

				{/* Col 2 — Content + Schedule */}
				<div className="lg:col-span-5 space-y-4">
					{/* Content */}
					<div className="bg-white rounded-2xl shadow-soft border border-border">
						<div className="px-4 py-3 border-b border-border">
							<h2 className="font-semibold">Contenu</h2>
						</div>
						<div className="p-4 space-y-4">
							<div className="space-y-1">
								<div className="flex items-center justify-between">
									<label className="text-xs font-semibold text-muted-foreground">Titre <span className="text-red-500">*</span></label>
									<div className="flex flex-col items-end gap-1">
										<div className="flex items-center gap-2 text-xs">
											<span className="text-muted-foreground">Langue</span>
											<select
												className={cn(
													'rounded-lg border bg-white px-2 py-1 text-xs focus:outline-hidden focus:ring-1 focus:ring-foreground',
													hasContentError ? 'border-red-500' : 'border-border',
												)}
												{...register('activeLang')}
											>
												<option value="FR">FR</option>
												<option value="EN">EN</option>
											</select>
										</div>
										{hasContentError && (
											<span className="text-xs text-red-500">
												Champs {isFR ? 'FR' : 'EN'} incomplets
											</span>
										)}
									</div>
								</div>
								<input
									className={cn(inputClass, !isFR && 'hidden')}
									placeholder="Titre en français"
									maxLength={100}
									{...register('titleFr', { required: 'Requis' })}
								/>
								{isFR && errors.titleFr && (
									<p className="text-xs text-destructive">{errors.titleFr.message}</p>
								)}
								<input
									className={cn(inputClass, isFR && 'hidden')}
									placeholder="Title in English"
									maxLength={100}
									{...register('titleEn', { required: 'Requis' })}
								/>
								{!isFR && errors.titleEn && (
									<p className="text-xs text-destructive">{errors.titleEn.message}</p>
								)}
							</div>

							<div className="space-y-1">
								<label className="text-xs font-semibold text-muted-foreground">Message <span className="text-red-500">*</span></label>
								<textarea
									className={cn(textareaClass, !isFR && 'hidden')}
									placeholder="Message en français"
									maxLength={300}
									rows={4}
									{...register('messageFr', { required: 'Requis' })}
								/>
								{isFR && errors.messageFr && (
									<p className="text-xs text-destructive">{errors.messageFr.message}</p>
								)}
								<textarea
									className={cn(textareaClass, isFR && 'hidden')}
									placeholder="Message in English"
									maxLength={300}
									rows={4}
									{...register('messageEn', { required: 'Requis' })}
								/>
								{!isFR && errors.messageEn && (
									<p className="text-xs text-destructive">{errors.messageEn.message}</p>
								)}
							</div>
						</div>
					</div>

					{/* Schedule */}
					<div className="bg-white rounded-2xl shadow-soft border border-border">
						<div className="px-4 py-3 border-b border-border">
							<h2 className="font-semibold">Planification</h2>
						</div>
						<div className="p-4 space-y-4">
							<div className="grid grid-cols-2 gap-3 items-end">
								<div className="space-y-1">
									<label className="text-xs font-semibold text-muted-foreground">Type de campagne <span className="text-red-500">*</span></label>
									<ControlledSelect
										control={control}
										name="campaignType"
										placeholder="Choisir un type"
										options={CAMPAIGN_TYPE_OPTIONS}
										rules={{ required: 'Requis' }}
									/>
								</div>
								{campaignType === BulkNotificationCampaignTypeEnum.ONE_TIME && (
									<DateTimePicker
										label="Date et heure d'envoi"
										field="scheduledAt"
										control={control}
										required
										validate={(date) => {
											if (!date) return 'Requis';
											if (date <= new Date()) return "La date doit être dans le futur";
											return true;
										}}
									/>
								)}
								{campaignType === BulkNotificationCampaignTypeEnum.RECURRENT && (
									<div className="space-y-1">
										<label className="text-xs font-semibold text-muted-foreground">Intervalle (jours) <span className="text-red-500">*</span></label>
										<ControlledInput
											control={control}
											name="intervalDays"
											type="number"
											placeholder="7"
											min={1}
											rules={{ required: 'Requis', min: { value: 1, message: 'Minimum 1 jour' } }}
										/>
									</div>
								)}
							</div>

							{campaignType === BulkNotificationCampaignTypeEnum.RECURRENT && (
								<div className="grid grid-cols-2 gap-3">
									<DateTimePicker
										label="Date de début"
										field="startDate"
										control={control}
										required
										validate={(date) => {
											if (!date) return 'Requis';
											if (date <= new Date()) return "La date doit être dans le futur";
											return true;
										}}
									/>
									<DateTimePicker
										label="Date de fin"
										field="endDate"
										control={control}
										required
										disabledBefore={startDate ?? undefined}
										validate={(date) => {
											if (!date) return 'Requis';
											if (date <= new Date()) return "La date doit être dans le futur";
											const startD = watch('startDate');
											if (startD && date <= startD) return "Doit être après la date de début";
											return true;
										}}
									/>
								</div>
							)}

							<Button
								type="button"
								className="w-full"
								disabled={isSubmitting}
								onClick={onSubmit}
							>
								<Send className="w-4 h-4 mr-2" />
								{isSubmitting ? 'Création...' : 'Créer la campagne'}
							</Button>
						</div>
					</div>
				</div>

				{/* Col 3 — Campaigns list */}
				<div className="lg:col-span-4 space-y-4">
					<UninstallsCard />
					<CampaignsList />
				</div>
			</div>
		</FormProvider>
	);
};

export default BulkNotificationPage;
